import React from 'react'
import styled from 'styled-components'

const CalculatorContainer = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 300px;
  z-index: 1000;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(0, 0, 0, 0.1);
  overflow: hidden;
`

const CalculatorHeader = styled.div`
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`

const CalculatorContent = styled.div`
  max-height: 400px;
  overflow-y: auto;
  padding: 8px 16px;
`

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #666;
  margin: 12px 0 8px;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  font-size: 13px;
`

const ItemName = styled.div`
  display: flex;
  align-items: center;
`

const ItemCount = styled.div`
  font-weight: 500;
`

const ColorSwatch = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  margin-right: 8px;
  background-color: ${props => props.color || '#ddd'};
  border: 1px solid rgba(0, 0, 0, 0.1);
`

const IconSwatch = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.03);
`

const TotalCount = styled.div`
  display: flex;
  justify-content: space-between;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  margin-top: 8px;
  padding-top: 8px;
  font-weight: 500;
`

function ObjectCalculator({ board, onClose }) {
  // Calculate object counts from the canvas
  const calculateObjectCounts = () => {
    if (!board?.canvas) return { materials: {}, stamps: {} };
    
    // Get only visible objects on the canvas
    const objects = board.canvas.getObjects().filter(obj => {
      // Filter out UI controls, temporary objects, and hidden objects
      // Allow groups IF they have stamp=true (custom stamps)
      const isStampGroup = obj.type === 'group' && obj.stamp === true;
      
      return obj.visible !== false && 
             !obj._controlsVisibility && // Not a control handle
             !obj.isToolCreated && // Not a temporary tool-created object
             obj.type !== 'activeSelection' && // Not a selection box
             (obj.type !== 'group' || isStampGroup) && // Allow stamp groups
             !obj.excludeFromExport && // Not an object marked to exclude from export
             obj.opacity !== 0 && // Not invisible
             obj !== board.canvas.backgroundImage && // Not the background image
             obj !== board.canvas.overlayImage; // Not the overlay image
    });
    
    if (process.env.NODE_ENV !== 'production') {
      console.log(`Total visible objects: ${objects.length}`);
    }
    
    // Count materials (polygons with material property)
    const materials = objects.reduce((acc, obj) => {
      if (obj.material) {
        if (process.env.NODE_ENV !== 'production') {
          console.log(`Found material object: ${obj.material}`);
        }
        acc[obj.material] = (acc[obj.material] || 0) + 1;
      }
      return acc;
    }, {});
    
    // Add detailed debugging for all objects
    if (process.env.NODE_ENV !== 'production') {
      objects.forEach((obj, index) => {
        console.log(`Object ${index}:`, {
          type: obj.type,
          stamp: obj.stamp,
          objectType: obj.objectType,
          stampName: obj.stampName,
          customStamp: obj.customStamp,
          fill: obj.fill,
          hasPath: !!obj.path
        });
      });
    }
    
    // Count stamps (objects with stamp=true)
    const stamps = {};
    
    // First pass to identify all unique objects and debug them
    objects.forEach(obj => {
      if (obj.stamp === true && obj.objectType === 'stamp') {
        const stampName = getStampNameFromObject(obj);
        const positionKey = `${Math.round(obj.left)}_${Math.round(obj.top)}`;
        if (process.env.NODE_ENV !== 'production') {
          console.log(`Found stamp: ${stampName}, pos: ${positionKey}`, {
            left: obj.left,
            top: obj.top,
            strokeWidth: obj.strokeWidth,
            stamp: obj.stamp,
            objectType: obj.objectType,
            _objects: obj._objects ? obj._objects.length : 'none'
          });
        }
      }
    });
    
    // Build sets of positions to detect duplicates (objects at the same position)
    const positionSets = {};
    
    // Second pass to count stamps, avoiding duplicates at the same position
    objects.forEach(obj => {
      if (obj.stamp === true && obj.objectType === 'stamp') {
        const stampName = getStampNameFromObject(obj);
        const positionKey = `${Math.round(obj.left)}_${Math.round(obj.top)}`;
        
        // Initialize position set for this stamp type if needed
        if (!positionSets[stampName]) {
          positionSets[stampName] = new Set();
        }
        
        // Only count if we haven't seen an object at this position before
        if (!positionSets[stampName].has(positionKey)) {
          positionSets[stampName].add(positionKey);
          stamps[stampName] = (stamps[stampName] || 0) + 1;
          if (process.env.NODE_ENV !== 'production') {
            console.log(`Counting stamp: ${stampName} at position ${positionKey}`);
          }
        } else if (process.env.NODE_ENV !== 'production') {
          console.log(`Skipping duplicate stamp: ${stampName} at position ${positionKey}`);
        }
      }
    });
    
    return { materials, stamps };
  };
  
  // Helper function to identify the stamp name from an object
  const getStampNameFromObject = (obj) => {
    // If stampName property exists (added in our update), use it directly
    if (obj.stampName) {
      return obj.stampName;
    }
    
    // Fallback method using color if stampName isn't available
    const { fill } = obj;
    
    // Color-based fallback mapping
    const colorToNameMap = {
      '#2E7D32': 'Small Tree',
      '#1B5E20': 'Large Tree',
      '#33691E': 'Palm Tree',
      '#388E3C': 'Bush',
      '#558B2F': 'Shrub',
      '#795548': 'Bench',
      '#8D6E63': 'Table',
      '#A1887F': 'Planter',
      '#F57C00': 'Umbrella',
    };
    
    return colorToNameMap[fill] || 'Other Object';
  };
  
  const { materials, stamps } = calculateObjectCounts();
  
  // Calculate totals
  const totalMaterials = Object.values(materials).reduce((sum, count) => sum + count, 0);
  const totalStamps = Object.values(stamps).reduce((sum, count) => sum + count, 0);
  const totalObjects = totalMaterials + totalStamps;
  
  return (
    <CalculatorContainer>
      <CalculatorHeader>
        <span>Object Calculator</span>
        <CloseButton onClick={onClose}>×</CloseButton>
      </CalculatorHeader>
      <CalculatorContent>
        {/* Materials Section */}
        {Object.keys(materials).length > 0 && (
          <>
            <SectionTitle>Materials</SectionTitle>
            {Object.entries(materials).map(([material, count]) => (
              <ItemRow key={material}>
                <ItemName>
                  <ColorSwatch color={getMaterialColor(material)} />
                  {material}
                </ItemName>
                <ItemCount>{count}</ItemCount>
              </ItemRow>
            ))}
            
            <ItemRow>
              <div>Total Materials</div>
              <ItemCount>{totalMaterials}</ItemCount>
            </ItemRow>
          </>
        )}
        
        {/* Stamps Section */}
        {Object.keys(stamps).length > 0 && (
          <>
            <SectionTitle>Stamps</SectionTitle>
            {Object.entries(stamps).map(([stampName, count]) => (
              <ItemRow key={stampName}>
                <ItemName>
                  <IconSwatch>
                    <svg width="16" height="16" viewBox="0 0 24 24">
                      <path 
                        d={getStampPath(stampName)} 
                        fill={getStampColor(stampName)}
                      />
                    </svg>
                  </IconSwatch>
                  {stampName}
                </ItemName>
                <ItemCount>{count}</ItemCount>
              </ItemRow>
            ))}
            
            <ItemRow>
              <div>Total Stamps</div>
              <ItemCount>{totalStamps}</ItemCount>
            </ItemRow>
          </>
        )}
        
        {/* Total Objects */}
        <TotalCount>
          <div>Total Objects</div>
          <div>{totalObjects}</div>
        </TotalCount>
      </CalculatorContent>
    </CalculatorContainer>
  );
}

// Helper functions to get material colors and stamp paths/colors
function getMaterialColor(materialName) {
  const MATERIAL_COLORS = {
    'Concrete': '#858585',
    'Wood': '#CD853F',
    'Metal': '#B8B8B8',
    'Glass': '#ADD8E6',
    'Plastic': '#F0E68C',
    'Stone': '#A9A9A9',
    'Brick': '#B22222',
    'Drywall': '#F5F5F5',
    'Ceramic': '#F5F5DC',
    'Carpet': '#008080',
    'Asphalt': '#2F4F4F',
    'Rubber': '#000000',
    'Vinyl': '#BA55D3',
    'Foam': '#FFE4B5',
    'Tile': '#F0FFF0',
    'Insulation': '#FFF8DC',
    'Composite': '#9370DB',
  };
  
  return MATERIAL_COLORS[materialName] || '#cccccc';
}

// Simplified mapping of stamp names to their properties
function getStampColor(stampName) {
  const stampColors = {
    'Small Tree': '#2E7D32',
    'Large Tree': '#1B5E20',
    'Palm Tree': '#33691E',
    'Bush': '#388E3C',
    'Shrub': '#558B2F',
    'Bench': '#795548',
    'Table': '#8D6E63',
    'Chair': '#8D6E63',
    'Umbrella': '#F57C00',
    'Planter': '#A1887F',
    'Other Object': '#999999',
    'Unknown Object': '#999999',
  };
  
  return stampColors[stampName] || '#999999';
}

function getStampPath(stampName) {
  // Simplified paths for icons
  const basicCircle = 'M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2Z';
  const basicSquare = 'M3,3H21V21H3V3Z';
  
  const stampPaths = {
    'Small Tree': 'M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2Z',
    'Large Tree': 'M12,2L4,12H6V19H18V12H20L12,2Z',
    'Palm Tree': 'M12,15L7,20H11V24H13V20H17L12,15Z',
    'Bush': basicCircle,
    'Shrub': 'M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z',
    'Bench': 'M4,2H20V8H4V2Z',
    'Table': 'M3,6H21V8H3V6Z',
    'Chair': 'M15,12V16H9V12H15Z',
    'Umbrella': 'M12,2A9,9 0 0,0 3,11H21A9,9 0 0,0 12,2Z',
    'Planter': 'M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2Z'
  };
  
  return stampPaths[stampName] || basicSquare;
}

export { ObjectCalculator }
